import { Outlet, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Sidebar from '@/layout/SideBar';
import { resolveRouteMeta } from '@/config/routesConfig';
import { Building2, Menu } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';

const AppLayout = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const { activeSecretary } = useAuth();
    const meta = resolveRouteMeta(location.pathname);

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    return (
        <div className="app-grid-glow flex h-screen w-screen overflow-hidden bg-[#0b0f1a]">
            {/* Sidebar permanentemente visível no desktop */}
            <div className="app-desktop-sidebar h-full w-[260px] shrink-0">
                <Sidebar />
            </div>

            {/* Sidebar mobile */}
            <div
                className={`app-mobile-navigation
                    fixed inset-0 z-50 transition-opacity duration-300
                    ${
                    isMobileMenuOpen
                        ? 'pointer-events-auto opacity-100'
                        : 'pointer-events-none opacity-0'
                }
                `}
            >
                {/* Fundo escuro */}
                <button
                    type="button"
                    aria-label="Fechar menu"
                    className="absolute inset-0 h-full w-full bg-black/60 backdrop-blur-sm"
                    onClick={closeMobileMenu}
                />

                {/* Menu lateral */}
                <div
                    className={`
                        absolute inset-y-0 left-0 w-[260px]
                        transform transition-transform duration-300
                        ${
                        isMobileMenuOpen
                            ? 'translate-x-0'
                            : '-translate-x-full'
                    }
                    `}
                >
                    <Sidebar
                        isMobile
                        onClose={closeMobileMenu}
                    />
                </div>
            </div>

            {/* Conteúdo */}
            <div className="flex min-w-0 flex-1 flex-col">
                <header className="app-shell-header relative flex min-h-[82px] shrink-0 items-center justify-between overflow-hidden border-b border-white/5 bg-[#111827]/80 px-4 backdrop-blur-xl md:px-8">
                    <div className="pointer-events-none absolute -left-10 top-0 h-24 w-56 rounded-full bg-blue-500/10 blur-3xl" />
                    <div className="flex items-center gap-4">
                        {/* Botão exibido somente no mobile */}
                        <button
                            type="button"
                            aria-label="Abrir menu"
                            aria-expanded={isMobileMenuOpen}
                            onClick={() => setIsMobileMenuOpen(true)}
                            className="app-mobile-trigger rounded-xl border border-white/5 bg-white/5 p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                        >
                            <Menu size={24} />
                        </button>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={location.pathname}
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 8 }}
                                transition={{ duration: 0.22 }}
                            >
                                <p className="text-[9px] font-black uppercase tracking-[0.28em] text-blue-400">{meta.eyebrow}</p>
                                <h1 className="app-page-title text-xl font-black uppercase italic tracking-tight md:text-2xl">{meta.title}</h1>
                                <p className="hidden text-xs text-slate-500 sm:block">{meta.description}</p>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {activeSecretary && (
                        <motion.div
                            initial={{ opacity: 0, x: 12 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="hidden max-w-[260px] items-center gap-3 rounded-2xl border border-white/5 bg-white/[0.03] px-4 py-2.5 md:flex"
                        >
                            <Building2 size={17} className="shrink-0 text-blue-400" />
                            <div className="min-w-0">
                                <p className="text-[8px] font-black uppercase tracking-widest text-slate-600">{activeSecretary.itTeam?.name || 'Equipe de TI'}</p>
                                <p className="truncate text-xs font-bold text-slate-300">{activeSecretary.name}</p>
                            </div>
                        </motion.div>
                    )}
                </header>

                <main className="custom-scrollbar flex-1 overflow-y-auto p-4 md:p-6 xl:p-8">
                    <div className="mx-auto w-full max-w-[1440px]">
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.div
                                key={location.pathname}
                                initial={{ opacity: 0, y: 14, filter: 'blur(4px)' }}
                                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                                exit={{ opacity: 0, y: -8, filter: 'blur(3px)' }}
                                transition={{ duration: 0.28, ease: 'easeOut' }}
                            >
                                <Outlet />
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AppLayout;
